import { useCallback, useEffect, useRef } from "react";

export type Order = "asc" | "desc";

export interface HeadCell<T> {
	id: keyof T;
	label: string;
	width: number;
}

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

export function getComparator<T>(order: Order, orderBy: keyof T): (a: T, b: T) => number {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

export function useRangeSelection(
	sortedRows: { id: number }[],
	selectedRows: Set<number>,
	setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>
) {
	const lastClickedIndexRef = useRef<number | null>(null);
	const selectedRowsRef = useRef(selectedRows);

	useEffect(() => {
		selectedRowsRef.current = selectedRows;
	});

	// Reset anchor whenever the visible sorted rows change (filter/sort)
	useEffect(() => {
		lastClickedIndexRef.current = null;
	}, [sortedRows]);

	const handleRowClick = useCallback(
		(rowId: number, index: number, shiftKey: boolean) => {
			if (shiftKey && lastClickedIndexRef.current !== null) {
				const start = Math.min(lastClickedIndexRef.current, index);
				const end = Math.max(lastClickedIndexRef.current, index);
				const rangeIds = sortedRows.slice(start, end + 1).map((r) => r.id);
				// Range follows the anchor row's state: if anchor was being selected, select all; otherwise deselect all
				const anchorId = sortedRows[lastClickedIndexRef.current].id;
				const selecting = selectedRowsRef.current.has(anchorId);
				setSelectedRows((prev) => {
					const next = new Set(prev);
					rangeIds.forEach((id) => (selecting ? next.add(id) : next.delete(id)));
					return next;
				});
			} else {
				lastClickedIndexRef.current = index;
				setSelectedRows((prev) => {
					const next = new Set(prev);
					next.has(rowId) ? next.delete(rowId) : next.add(rowId);
					return next;
				});
			}
		},
		[sortedRows, setSelectedRows]
	);

	return { handleRowClick };
}

export const cellStyle = {
	py: 1,
	px: 1.5,
	borderRight: 1,
	borderBottom: 1,
	borderColor: "border.hover",
	whiteSpace: "nowrap"
};


